import { api } from '.';
import { PaymentMethod } from '../customers/types';
import { IGiftCardLineItem } from '../gift-cards/types';
import { Credit } from './ScheduleApi';

export enum TransactionStatus {
  Initial = 'Initial',
  Captured = 'Captured',
  Refunded = 'Refunded',
  PartiallyRefunded = 'PartiallyRefunded',
  Canceled = 'Canceled',
  Failed = 'Failed',
  Pending = 'Pending',
}

export enum TransactionType {
  Payment = 'Payment',
  Refund = 'Refund',
  Tip = 'Tip',
}

export enum LineItemType {
  Treatment = 'Treatment',
  AddOn = 'AddOn',
  CancellationPolicyAdjustment = 'CancellationPolicyAdjustment',
  Product = 'Product',
  Discount = 'Discount',
  Other = 'Other',
}

export interface IOrderTransaction {
  id: string;
  amount: number;
  status: TransactionStatus;
  failReason: string;
  gateway: string; // enum
  paymentMethod: PaymentMethod;
  transactionType: TransactionType;
  capturedUtc: Date | null;
  failedUtc: Date | null;
  tipAmount: number;
  refundPaymentId: string;
  memo: string | null;
  paymentSourceId: string | null;
  // The gift card code the order recorded when it charged the card (#2216). Null on anything that is
  // not a gift card payment, and on payments captured before that shipped.
  giftCardCode: string | null;
  marketplaceCharge: number | null;
}

export interface IOrderLineItem {
  id: string;
  name: string;
  price: number;
  created?: Date;
  description?: string;
  duration?: number;
  scheduledFor?: Date;
  type?: LineItemType;
  updated?: Date | null;
  voided?: boolean;
}

export interface IOrder {
  id: string;
  customerId: string;
  bookingId: string;
  locationId: string;
  timeZone: string;
  state: string; // enum
  primaryCardId: string | null;
  total: number;
  totalPaid: number;
  balance: number;
  failureReason: string;
  transactions: IOrderTransaction[];
  lineItems: IOrderLineItem[];
  createdUtc: Date | null;
  tipAmountPaid: number;
}

export interface IGetCustomerOrdersRequest {
  customerId: string;
  locationIds: string[];
}

export interface IPaymentRequest {
  orderId: string;
  paymentToken?: string;
  chargeCardOnFile: boolean;
  saveCardForFutureBookings: boolean;
  paymentMethod: PaymentMethod;
  cardId?: string;
  memo: string;
  credits?: Credit[];
}

export interface IProcessTipRequest extends IPaymentRequest {
  customerId: string;
  tipAmountInDollars: number;
}

export interface IProcessPaymentRequest extends IPaymentRequest {
  amountInDollars: number;
  tipAmount: number;
}

export interface IAddLineItemRequest {
  orderId: string;
  name: string;
  price: number;
  description: string;
  type: string;
}

export interface IVoidLineItemRequest {
  orderId: string;
  lineItemId: string;
  description: string;
}

export interface IRefundPaymentRequest {
  orderId: string;
  paymentId: string;
  amount: number;
  memo: string;
}

export interface ICancelCheckoutRequest {
  orderId: string;
  paymentId: string;
}

export interface IRefundOrderRequest {
  orderId: string;
  memo: string;
}

export interface IOrderTreatment {
  name: string;
  price: number;
}

export interface IOrderAddOn {
  name: string;
  price: number;
}

export interface IOrderTimeSlot {
  starts: string;
  durationInMinutes: number;
  timeZone: string;
}

export interface ICreateOrderRequest {
  businessId: string;
  customerId: string;
  locationId: string;
  customerName: string;
  customerEmail: string;
  addOns: IOrderAddOn[];
  treatments: IOrderTreatment[];
  timeSlot: IOrderTimeSlot;
  timeZone: string;
}

export interface IInitiatePaymentRequest {
  orderId: string;
  amountInDollars: number;
  tipAmount: number;
  memo: string;
}

export interface IInitiateTipRequest {
  orderId: string;
  tipAmountInDollars: number;
  memo: string;
}

export interface IUpdatePaymentRequest extends IInitiatePaymentRequest {
  cardId: string | null;
  chargeCardOnFile: boolean;
  credits: Credit[] | null;
  transactionId: string;
}

export interface IUpdateTipRequest extends IInitiateTipRequest {
  cardId: string | null;
  chargeCardOnFile: boolean;
  credits: Credit[] | null;
  tipId: string;
}

export interface ICapturePaymentRequest extends IInitiatePaymentRequest {
  credits: Credit[] | null;
  transactionId: string;
  saveCardForFutureBookings: boolean;
}

export interface ICaptureTipRequest extends IInitiateTipRequest {
  credits: Credit[] | null;
  tipId: string;
  saveCardForFutureBookings: boolean;
}

export interface IUpdateOrderBeforeDownPaymentRequest {
  orderId: string;
  cardId: string | null;
  chargeCardOnFile: boolean;
  credits: Credit[] | null;
  businessId: string;
  customerId: string;
  addOns: IOrderAddOn[];
  treatments: IOrderTreatment[];
  timeSlot: IOrderTimeSlot;
}

export interface IInitialOrderResponse {
  location: string;
  clientSecret: string;
  marketplaceFeeCharge: number;
}

export interface IOrderBeforeProcess {
  orderId: string;
  clientSecret: string;
  marketplaceFeeCharge: number;
}

export interface IInitialPaymentResponse {
  location: string;
  clientSecret: string;
}

export interface IPaymentBeforeProcess {
  transactionId: string;
  clientSecret: string;
}

export interface ICreateGiftCardOrderRequest {
  businessId: string;
  locationId: string;
  customerName: string;
  customerEmail: string;
  timeZone: string;
  lineItems: IGiftCardLineItem[];
}

export interface IUpdateGiftCardOrderRequest {
  orderId: string;
  cardId: string | null;
  chargeCardOnFile: boolean;
  lineItems: IGiftCardLineItem[];
}

// Define a service using a base URL and expected endpoints
export const orderApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getOrder: builder.query<IOrder, string>({
      query: (orderId) => {
        if (!orderId) throw new Error('bookingId is required.');
        return {
          url: `/order/${orderId}`,
          method: 'GET',
        };
      },
      providesTags: ['order'],
      transformResponse: (response: IOrder) => {
        return response;
      },
    }),
    getBookingOrder: builder.query<IOrder, string>({
      query: (bookingId) => {
        if (!bookingId) throw new Error('bookingId is required.');
        return {
          url: `/order/booking/${bookingId}`,
          method: 'GET',
        };
      },
      providesTags: ['booking-order'],
      transformResponse: (response: IOrder) => {
        return response;
      },
    }),
    getLocationOrders: builder.query<IOrder[], string>({
      query: (locationId) => {
        if (!locationId) throw new Error('locationId is required.');
        return {
          url: `/order/location/${locationId}`,
          method: 'GET',
        };
      },
      providesTags: ['location-orders'],
      transformResponse: (response: IOrder[]) => {
        return response;
      },
    }),
    getAllLocationCustomerOrders: builder.query<
      IOrder[],
      IGetCustomerOrdersRequest
    >({
      queryFn: async (request, _queryApi, _extraOptions, baseQuery) => {
        let error = '';
        let orders: IOrder[] = [];

        await Promise.all(
          request.locationIds.map(async (locatioId) => {
            if (error) return;
            const response = await baseQuery({
              url: `/order/location/${locatioId}/customer/${request.customerId}`,
              method: `get`,
            });

            if (response.error) {
              error = response.error as string;
              return;
            }

            const data = response.data as IOrder[];
            orders = orders.concat(data);
          })
        );

        // filter out orders that were created but not used (ie. abandoned or expired reservations)
        return {
          data: orders.filter(
            (o) =>
              o.state.toLowerCase() !== 'initial' &&
              o.bookingId !== '00000000-0000-0000-0000-000000000000'
          ),
        };
      },
      providesTags: ['customer-orders'],
    }),
    addTip: builder.mutation<void, IProcessTipRequest>({
      query: (data) => {
        return {
          url: `/order/${data.orderId}/tip`,
          method: 'POST',
          data,
        };
      },
      invalidatesTags: [
        'booking-order',
        'customer-orders',
        'location-orders',
        'customer-cards',
      ],
    }),
    addPayment: builder.mutation<void, IProcessPaymentRequest>({
      query: (data) => {
        return {
          url: `/order/${data.orderId}/payment`,
          method: 'POST',
          data,
        };
      },
      invalidatesTags: [
        'booking-order',
        'customer-orders',
        'location-orders',
        'customer-cards',
      ],
    }),
    addLineItem: builder.mutation<void, IAddLineItemRequest>({
      query: ({ orderId, ...data }) => {
        return {
          url: `/order/${orderId}/line-item`,
          method: 'POST',
          data,
        };
      },
      invalidatesTags: ['booking-order', 'customer-orders', 'location-orders'],
    }),
    voidLineItem: builder.mutation<void, IVoidLineItemRequest>({
      query: ({ orderId, lineItemId, ...data }) => {
        return {
          url: `/order/${orderId}/line-item/${lineItemId}`,
          method: 'DELETE',
          data,
        };
      },
      invalidatesTags: ['booking-order', 'customer-orders', 'location-orders'],
    }),
    refundPayment: builder.mutation<void, IRefundPaymentRequest>({
      query: (data) => {
        return {
          url: `/order/${data.orderId}/payment/${data.paymentId}/refund`,
          method: 'POST',
          data: {
            refundAmount: data.amount,
            memo: data.memo,
          },
        };
      },
      invalidatesTags: ['booking-order', 'customer-orders', 'location-orders'],
    }),
    cancelCheckout: builder.mutation<void, ICancelCheckoutRequest>({
      query: (data) => {
        return {
          url: `/order/${data.orderId}/checkout/${data.paymentId}`,
          method: 'DELETE',
        };
      },
    }),
    refundOrder: builder.mutation<void, IRefundOrderRequest>({
      query: (data) => {
        return {
          url: `/order/${data.orderId}/refund`,
          method: 'POST',
          data: { memo: data.memo },
        };
      },
      invalidatesTags: ['booking-order', 'customer-orders', 'location-orders'],
    }),
    createOrder: builder.mutation<IOrderBeforeProcess, ICreateOrderRequest>({
      query: (data) => {
        return {
          url: `/order`,
          method: 'POST',
          data,
        };
      },
      transformResponse: (response: IInitialOrderResponse) => {
        const result: IOrderBeforeProcess = {
          clientSecret: response.clientSecret,
          marketplaceFeeCharge: response.marketplaceFeeCharge,
          orderId: response.location.split('/')[2],
        };

        return result;
      },
    }),
    initiatePayment: builder.mutation<
      IPaymentBeforeProcess,
      IInitiatePaymentRequest
    >({
      query: (data) => {
        return {
          url: `/order/${data.orderId}/payment-v2`,
          method: 'POST',
          data,
        };
      },
      transformResponse: (response: IInitialPaymentResponse) => {
        const result: IPaymentBeforeProcess = {
          clientSecret: response.clientSecret,
          transactionId: response.location.split('/')[4],
        };

        return result;
      },
      invalidatesTags: ['order'],
    }),
    initiateTip: builder.mutation<IPaymentBeforeProcess, IInitiateTipRequest>({
      query: (data) => {
        return {
          url: `/order/${data.orderId}/tip-v2`,
          method: 'POST',
          data,
        };
      },
      transformResponse: (response: IInitialPaymentResponse) => {
        const result: IPaymentBeforeProcess = {
          clientSecret: response.clientSecret,
          transactionId: response.location.split('/')[4],
        };

        return result;
      },
      invalidatesTags: ['order'],
    }),
    updateOrderBeforeDownPayment: builder.mutation<
      IOrderBeforeProcess,
      IUpdateOrderBeforeDownPaymentRequest
    >({
      query: (data) => {
        return {
          url: `/order/${data.orderId}`,
          method: 'PUT',
          data,
        };
      },
      transformResponse: (response: IInitialOrderResponse) => {
        const result: IOrderBeforeProcess = {
          clientSecret: response.clientSecret,
          marketplaceFeeCharge: response.marketplaceFeeCharge,
          orderId: response.location.split('/')[2],
        };

        return result;
      },
    }),
    updatePayment: builder.mutation<
      IPaymentBeforeProcess,
      IUpdatePaymentRequest
    >({
      query: (data) => {
        return {
          url: `/order/${data.orderId}/payment-v2/${data.transactionId}`,
          method: 'PUT',
          data,
        };
      },
      transformResponse: (response: IInitialPaymentResponse) => {
        const result: IPaymentBeforeProcess = {
          clientSecret: response.clientSecret,
          transactionId: response.location.split('/')[4],
        };

        return result;
      },
      invalidatesTags: ['order'],
    }),
    updateTip: builder.mutation<IPaymentBeforeProcess, IUpdateTipRequest>({
      query: (data) => {
        return {
          url: `/order/${data.orderId}/tip-v2/${data.tipId}`,
          method: 'PUT',
          data,
        };
      },
      transformResponse: (response: IInitialPaymentResponse) => {
        const result: IPaymentBeforeProcess = {
          clientSecret: response.clientSecret,
          transactionId: response.location.split('/')[4],
        };

        return result;
      },
      invalidatesTags: ['order'],
    }),
    capturePayment: builder.mutation<void, ICapturePaymentRequest>({
      query: (data) => {
        return {
          url: `/order/${data.orderId}/payment-v2/${data.transactionId}`,
          method: 'POST',
          data,
        };
      },
      invalidatesTags: [
        'order',
        'booking-order',
        'customer-orders',
        'location-orders',
        'customer-cards',
      ],
    }),
    captureTip: builder.mutation<void, ICaptureTipRequest>({
      query: (data) => {
        return {
          url: `/order/${data.orderId}/tip-v2/${data.tipId}`,
          method: 'POST',
          data,
        };
      },
      invalidatesTags: [
        'order',
        'booking-order',
        'customer-orders',
        'location-orders',
        'customer-cards',
      ],
    }),
    createGiftCardOrder: builder.mutation<
      IOrderBeforeProcess,
      ICreateGiftCardOrderRequest
    >({
      query: (data) => {
        return {
          url: `/gift-card-order`,
          method: 'POST',
          data,
        };
      },
      transformResponse: (response: IInitialOrderResponse) => {
        const result: IOrderBeforeProcess = {
          clientSecret: response.clientSecret,
          marketplaceFeeCharge: response.marketplaceFeeCharge,
          orderId: response.location.split('/')[2],
        };

        return result;
      },
    }),
    updateGiftCardOrder: builder.mutation<
      IOrderBeforeProcess,
      IUpdateGiftCardOrderRequest
    >({
      query: ({ orderId, ...data }) => {
        return {
          url: `/gift-card-order/${orderId}`,
          method: 'PUT',
          data,
        };
      },
      transformResponse: (response: IInitialOrderResponse) => {
        const result: IOrderBeforeProcess = {
          clientSecret: response.clientSecret,
          marketplaceFeeCharge: response.marketplaceFeeCharge,
          orderId: response.location.split('/')[2],
        };

        return result;
      },
    }),
    completeGiftCardOrder: builder.mutation<void, string>({
      query: (orderId) => {
        return {
          url: `/gift-card-order/${orderId}`,
          method: 'POST',
          data: {
            SaveCard: false,
          },
        };
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetBookingOrderQuery,
  useLazyGetBookingOrderQuery,
  useGetLocationOrdersQuery,
  useGetAllLocationCustomerOrdersQuery,
  useAddTipMutation,
  useAddPaymentMutation,
  useAddLineItemMutation,
  useVoidLineItemMutation,
  useRefundPaymentMutation,
  useCancelCheckoutMutation,
  useRefundOrderMutation,
  useCreateOrderMutation,
  useUpdateOrderBeforeDownPaymentMutation,
  useInitiatePaymentMutation,
  useUpdatePaymentMutation,
  useCapturePaymentMutation,
  useGetOrderQuery,
  useLazyGetOrderQuery,
  useInitiateTipMutation,
  useCaptureTipMutation,
  useUpdateTipMutation,
  useCreateGiftCardOrderMutation,
  useUpdateGiftCardOrderMutation,
  useCompleteGiftCardOrderMutation,
} = orderApi;
